import { useMemo, useState } from "react";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useTenantScope } from "../../hooks/useTenantScope";
import { BenefitsMetricsTenantScopeHeader } from "./components/BenefitsMetricsWidgets";
import { Dashboard } from "./Dashboard";
import { AuditReport } from "./AuditReport";
import { useGetAuditReportCatalogQuery } from "../../store/services/reports";
import "./reports.css";

/** El export de eventos ya existía y tiene su propia pantalla. */
const EVENT_METRICS = "event_metrics";

export const ReportsConfig = () => {
  const scope = useTenantScope();
  // El listado de reportes de auditoría lo manda el backend: sumar un reporte
  // al catálogo lo hace aparecer acá sin tocar el front. Las entradas son
  // MÉTRICAS, no períodos: el bucket de tiempo (semana/mes) es un control de la
  // pantalla del reporte, no una opción de este selector.
  const { data: auditReports = [] } = useGetAuditReportCatalogQuery();
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const options = useMemo(
    () => [
      { label: "Reporte de eventos", value: EVENT_METRICS },
      ...auditReports.map((report) => ({ label: report.label, value: report.key })),
    ],
    [auditReports]
  );

  const handleTypeChange = (event: DropdownChangeEvent) => {
    const value = typeof event.value === "string" ? event.value : null;
    setSelectedType(options.some((option) => option.value === value) ? value : null);
  };

  const selectedAuditReport =
    selectedType && selectedType !== EVENT_METRICS
      ? auditReports.find((report) => report.key === selectedType)
      : undefined;

  // Un solo lugar donde se decide qué reporte se muestra: el envoltorio de card
  // que lo contiene es el mismo para los dos y no vale la pena duplicarlo.
  const report =
    selectedType === EVENT_METRICS ? (
      <Dashboard />
    ) : selectedAuditReport ? (
      <AuditReport
        key={selectedAuditReport.key}
        report={selectedAuditReport.key}
        label={selectedAuditReport.label}
        grains={selectedAuditReport.grains}
      />
    ) : null;

  return (
    /* SYSTEM_DESIGN.md §7, "Layout base: card blanca elevada sobre fondo gris
       claro": la pantalla es UNA card, sin franja gris entre bloques.

       El padding y el alto salen de `reports.css`: §5.1 fija el padding interno
       de cards (32px 40px 40px) y la card crece hasta el fondo de la pantalla. */
    <div className="p-card reports-page">
      {/* §6.1 — título de la pantalla centrado. */}
      <div className="flex flex-column align-items-center w-full gap-2 mb-4">
        <h1 className="text-center m-0">Reportes</h1>
        <BenefitsMetricsTenantScopeHeader tenantId={scope.tenantId} />
      </div>

      {/* El selector vive DENTRO de la card del reporte, arriba de todo: es el
          control que decide qué reporte se está viendo, así que pertenece al
          bloque del reporte y no al encabezado de la pantalla.

          La card se dibuja siempre, incluso sin nada elegido: si solo existiera
          con un reporte cargado, el selector no tendría dónde vivir al entrar. */}
      <div className="reports-page__report">
        <div className="flex flex-column mb-4" style={{ maxWidth: "20rem", gap: 6 }}>
          <label htmlFor="reportType" className="text-600 text-sm">
            Tipos
          </label>
          <Dropdown
            inputId="reportType"
            value={selectedType}
            options={options}
            optionLabel="label"
            optionValue="value"
            placeholder="Seleccioná un tipo"
            showClear
            appendTo="self"
            className="w-full"
            onChange={handleTypeChange}
          />
        </div>

        {report ?? (
          <p className="text-color-secondary m-0">
            Seleccioná un tipo para ver el reporte.
          </p>
        )}
      </div>
    </div>
  );
};
